import type { TabRestriction } from '@operations/hooks/useActiveTab/types';
import type { RoleName } from '@store/skeletor/roles/types';
import { SALES_EXECUTIVE_ROLE } from '@store/skeletor/roles/types';
import type { Rut } from '@utils/rut/types';

const OPERATION_QUERY_PARAMS_KEY = {
  ACTIVE_TAB: 'active',
};

const OPERATION_DETAIL_TABS = {
  SUMMARY: 'summary',
  INVOICES: 'invoices',
  DEBTORS: 'debtors',
  TERMS_AND_CONDITIONS: 'terms-and-conditions',
  ATTACHMENTS: 'attachments',
} as const;

const OPERATION_DETAIL_TAB_LABELS: Record<
  keyof typeof OPERATION_DETAIL_TABS,
  string
> = {
  SUMMARY: 'Resumen',
  INVOICES: 'Riesgo',
  DEBTORS: 'Deudores',
  TERMS_AND_CONDITIONS: 'Términos y condiciones',
  ATTACHMENTS: 'Adjuntos',
};

const OPERATIONS_PATHS = {
  LIST: '/operations',
  DETAIL_HOME: (id: string) => `/operations/${id}`,
  VIEW: {
    DEFAULT: (id: string) => `/operations/${id}/view`,
    SUMMARY: (id: string) =>
      `/operations/${id}/view?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.SUMMARY}`,
    INVOICES: (id: string) =>
      `/operations/${id}/view?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.INVOICES}`,
    DEBTORS: (id: string) =>
      `/operations/${id}/view?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.DEBTORS}`,
    REJECTED: (id: string) => `/operations/${id}/rejected`,
    CLOSED: (id: string) => `/operations/${id}/closed`,
  },
  DEBTORS: {
    OVERVIEW: (operationId: string, debtorId: string) =>
      `/operations/${operationId}/debtors/${debtorId}`,
  },
  STATE_IN_VERIFICATION: {
    DEFAULT: (id: string) => `/operations/${id}/in-verification`,
    SUMMARY: (id: string) =>
      `/operations/${id}/in-verification?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.SUMMARY}`,
    INVOICES: (id: string) =>
      `/operations/${id}/in-verification?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.INVOICES}`,
    DEBTORS: (id: string) =>
      `/operations/${id}/in-verification?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.DEBTORS}`,
  },
  STATE_IN_TREASURY: {
    DEFAULT: (id: string) => `/operations/${id}/in-treasury`,
    SUMMARY: (id: string) =>
      `/operations/${id}/in-treasury?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.SUMMARY}`,
    INVOICES: (id: string) =>
      `/operations/${id}/in-treasury?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.INVOICES}`,
    DEBTORS: (id: string) =>
      `/operations/${id}/in-treasury?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.DEBTORS}`,
  },
  STATE_IN_DEPOSIT: {
    DEFAULT: (id: string) => `/operations/${id}/in-deposit`,
    SUMMARY: (id: string) =>
      `/operations/${id}/in-deposit?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.SUMMARY}`,
    INVOICES: (id: string) =>
      `/operations/${id}/in-deposit?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.INVOICES}`,
    DEBTORS: (id: string) =>
      `/operations/${id}/in-deposit?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.DEBTORS}`,
  },
  STATE_NEW: {
    DEFAULT: (id: string) => `/operations/${id}/new`,
    SUMMARY: (id: string) =>
      `/operations/${id}/new?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.SUMMARY}`,
    INVOICES: (id: string) =>
      `/operations/${id}/new?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.INVOICES}`,
    DEBTORS: (id: string) =>
      `/operations/${id}/new?${OPERATION_QUERY_PARAMS_KEY.ACTIVE_TAB}=${OPERATION_DETAIL_TABS.DEBTORS}`,
  },
  INVOICES: (operationId: string) => `/operations/${operationId}/invoices`,
  INVOICE_DETAILS: (operationId: string, invoiceId: string) =>
    `/operations/${operationId}/invoices/${invoiceId}`,
  TERMS_AND_CONDITIONS: (operationId: string) =>
    `/operations/${operationId}/terms-conditions`,
  CREATE: ({
    executiveEmail,
    clientRut,
  }: {
    executiveEmail: string;
    clientRut: Rut;
  }) =>
    `/operations/new?executiveEmail=${executiveEmail}&clientRut=${clientRut}`,
};

export {
  OPERATION_DETAIL_TAB_LABELS,
  OPERATION_DETAIL_TABS,
  OPERATION_QUERY_PARAMS_KEY,
  OPERATIONS_PATHS,
};

const DEBTORS_EXCLUDED_ROLES: RoleName[] = [SALES_EXECUTIVE_ROLE];

const OPERATION_TAB_RESTRICTIONS: TabRestriction[] = [
  { tab: OPERATION_DETAIL_TABS.DEBTORS, excludeRoles: DEBTORS_EXCLUDED_ROLES },
];

export { DEBTORS_EXCLUDED_ROLES, OPERATION_TAB_RESTRICTIONS };
